import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as appState from '@smarsh-cyber/app-state';
import {
  LOGIN_SOURCE,
  NGP_LOGIN,
  PORTAL_LOGOUT
} from '@smarsh-cyber/shared/constants';
import { PortalConfig } from '@smarsh-cyber/shared/models';
import {
  buildTokenKey,
  getBasePortal,
  isCognitoLogin,
  isDrupalLogin,
  getLoginSource,
  isNgpLogin,
  setLoginSource
} from '@smarsh-cyber/shared/utils';
import { catchError, map, Observable, of } from 'rxjs';
import { PORTAL_CONFIG } from '../../app.constants';
import { WindowsService } from '../../shared/services';
import {
  LoginRES,
  ResetPasswordREQ,
  ResetPasswordRES,
  VerifyOtpREQ,
  VerifyOtpRES,
  VerifyUserREQ,
  VerifyUserRES
} from '../models/api_models';
import { ResetPasswordModel } from '../models/ui_models';
import {
  translateErrorRes,
  translateForgetPasswordRes,
  translateLoginRes,
  translateResetPasswordErrorRes,
  translateVerifyOTPErrorRes,
  translateVerifyOtpRes,
  translateVerifyUserErrorRes,
  translateVerifyEmailRes as translateVerifyUserRes
} from '../utils/auth.translators';
import { AuthDataService } from './auth.data.service';
import {
  GetLoginOtpREQ,
  GetLoginOtpRES
} from '../models/api_models/passcode/get-login-otp.res';
import {
  VerifyLoginOtpREQ,
  VerifyLoginOtpRES
} from '../models/api_models/passcode/verify-login-otp.res';
import {
  translateGetLoginOtpRes,
  translateGetLoginOtpErrorRes,
  translateVerifyLoginOtpRes,
  translateVerifyLoginOTPErrorRes
} from '../utils/auth-otp.translators';
import { AppSessionService } from '@smarsh-cyber/core';

@Injectable({
  providedIn: 'root'
})
export class AuthUiService {
  #store = inject(Store);

  userDetails$ = this.#store.selectSignal(appState.selectUser);
  userToken$ = this.#store.selectSignal(appState.selectUserToken);

  windowService = inject(WindowsService);
  #router = inject(Router);

  constructor(
    private authDataService: AuthDataService,
    private appSession: AppSessionService
  ) {}

  login(credentials: { username: string; password: string }) {
    return this.authDataService.login(credentials).pipe(
      map((response: LoginRES) => {
        return translateLoginRes(response);
      }),
      catchError((error) => {
        return of(translateErrorRes(error));
      })
    );
  }

  verifyUser(req: VerifyUserREQ) {
    return this.authDataService.verifyUser(req).pipe(
      map((response: VerifyUserRES) => {
        return translateVerifyUserRes(response);
      }),
      catchError((error) => {
        return of(translateVerifyUserErrorRes(error));
      })
    );
  }

  verifyOtp(req: VerifyOtpREQ) {
    return this.authDataService.verifyOtp(req).pipe(
      map((response: VerifyOtpRES) => {
        return translateVerifyOtpRes(response);
      }),
      catchError((error) => {
        return of(translateVerifyOTPErrorRes(error));
      })
    );
  }

  resetPassword(req: ResetPasswordREQ): Observable<ResetPasswordModel> {
    return this.authDataService.resetPassword(req).pipe(
      map((response: ResetPasswordRES) => {
        return translateForgetPasswordRes(response);
      }),
      catchError((error) => {
        return of(translateResetPasswordErrorRes(error));
      })
    );
  }

  logoutAndRedirect(
    portalName: string,
    portalUrl: string,
    rediretAfterLogout = true
  ) {
    const basePortal = getBasePortal(portalName);
    const portalConfig: PortalConfig = PORTAL_CONFIG[basePortal];
    const loginSource: LOGIN_SOURCE = getLoginSource(portalName);
    this.windowService.clearSessionStorage();
    this.windowService.clearLocalStorage();
    this.appSession.destroy();
    if (!rediretAfterLogout) setLoginSource(loginSource, portalName); //maintain loginSource in localstorage if not redirecting to login page
    if (portalConfig && rediretAfterLogout)
      if (loginSource) {
        if (isNgpLogin(loginSource)) this.redirectToNgpLogin(portalName);
        else if (isDrupalLogin(loginSource))
          this.redirectToDrupalLogin(portalUrl);
        else if (isCognitoLogin(loginSource))
          this.redirectToCognitoLogin(portalConfig, portalUrl, portalName);
      } else {
        if (portalConfig.portalLogin) this.redirectToDrupalLogin(portalUrl);
        else if (portalConfig.ngpLogin) this.redirectToNgpLogin(portalName);
        else if (portalConfig.cognitoUrl)
          this.redirectToCognitoLogin(portalConfig, portalUrl, portalName);
      }
  }

  redirectToNgpLogin(portalName: string) {
    const urlTree = this.#router.createUrlTree([NGP_LOGIN], {
      queryParams: { portalName },
      queryParamsHandling: 'preserve'
    });
    const target = this.#router.serializeUrl(urlTree);
    const current = this.#router.serializeUrl(
      this.#router.parseUrl(this.#router.url)
    );
    if (current === target) return;

    // prefer in-app navigation; if it fails, fallback to full reload
    this.#router
      .navigateByUrl(urlTree)
      .catch(() => (window.location.href = target));
  }

  redirectToDrupalLogin(portalUrl: string) {
    const loginUrl = portalUrl.concat(PORTAL_LOGOUT);
    window.location.href = loginUrl;
  }
  redirectToCognitoLogin(
    portalConfig: PortalConfig,
    portalUrl: string,
    portalName: string
  ) {
    if (portalConfig.cognitoUrl) window.location.href = portalConfig.cognitoUrl;
    else if (portalConfig.portalLogin) this.redirectToDrupalLogin(portalUrl);
    else this.redirectToNgpLogin(portalName);
  }

  setAuthToken(token: string, portalName: string): void {
    const tokenKey = buildTokenKey(portalName);
    this.windowService.setToLocalStorage(tokenKey, token);
  }

  getLoginOtp(req: GetLoginOtpREQ) {
    return this.authDataService.getLoginOtp(req).pipe(
      map((response: GetLoginOtpRES) => {
        return translateGetLoginOtpRes(response);
      }),
      catchError((error) => {
        return of(translateGetLoginOtpErrorRes(error));
      })
    );
  }

  verifyLoginOtp(req: VerifyLoginOtpREQ) {
    return this.authDataService.verifyLoginOtp(req).pipe(
      map((response: VerifyLoginOtpRES) => {
        return translateVerifyLoginOtpRes(response);
      }),
      catchError((error) => {
        return of(translateVerifyLoginOTPErrorRes(error));
      })
    );
  }
}
